import React, { useEffect, useRef, useCallback, useState, type RefObject, type WeakValidationMap } from 'react';
import { PropTypes, describe } from '@elliemae/ds-props-helpers';
import { Grid } from '@elliemae/ds-grid';
import { Loader } from './Loader.js';

type UseMakeMutable = <T>(referenceVar: T) => React.MutableRefObject<T>;

const useMakeMutable: UseMakeMutable = (referenceVar) => {
  const mutable = useRef(referenceVar);
  useEffect(() => {
    mutable.current = referenceVar;
  }, [referenceVar]);
  return mutable;
};

interface Props {
  isFetching: boolean;
  hasMoreItems?: boolean;
  fetchData: () => void;
  children: React.ReactNode;
  height?: number | string;
}

interface Args extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

function useIntersectionObserver(
  elementRef: RefObject<Element>,
  { threshold = 0, root = null, rootMargin = '0%', freezeOnceVisible = false }: Args,
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  onIntersectionCb = (entry: IntersectionObserverEntry): void => {},
): IntersectionObserverEntry | undefined {
  const mutableIntersectionCb = useMakeMutable(onIntersectionCb);
  const [entry, setEntry] = useState<IntersectionObserverEntry>();

  const frozen = entry?.isIntersecting && freezeOnceVisible;

  const updateEntry = ([newEntry]: IntersectionObserverEntry[]): void => {
    setEntry(newEntry);
    if (newEntry.isIntersecting) mutableIntersectionCb.current(newEntry);
  };

  useEffect(() => {
    const node = elementRef?.current; // DOM Ref
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || frozen || !node) return () => {};

    const observerParams = { threshold, root, rootMargin };
    const observer = new IntersectionObserver(updateEntry, observerParams);

    observer.observe(node);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, JSON.stringify(threshold), root, rootMargin, frozen]);

  return entry;
}

const InfiniteLoader = ({ isFetching, fetchData, children, height, hasMoreItems = true }: Props) => {
  const [viewportHeight, setViewportHeight] = useState<number | undefined | string>(height);

  const baselineRef = useRef<HTMLDivElement | null>(null);
  const mutableIsFetching = useMakeMutable(isFetching);
  const mutableFetchData = useMakeMutable(fetchData);
  const onIntersectionCb = React.useCallback(() => {
    if (hasMoreItems && !mutableIsFetching.current) {
      mutableFetchData.current();
    }
  }, [hasMoreItems, mutableFetchData, mutableIsFetching]);
  useIntersectionObserver(
    baselineRef,
    {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    },
    onIntersectionCb,
  );

  const handleResize = useCallback(() => {
    setViewportHeight(window.innerHeight);
  }, []);

  useEffect(() => {
    if (!height) {
      window.addEventListener('resize', handleResize);
      handleResize();
    }
    return () => {
      if (!height) window.removeEventListener('resize', handleResize);
    };
  }, [handleResize, height]);

  return (
    <Grid style={{ position: 'relative', overflow: 'hidden', height: viewportHeight }}>
      <Grid style={{ overflow: isFetching ? 'hidden' : 'auto', height: viewportHeight }}>
        {children}
        <div ref={baselineRef} style={{ height: 1 }} />
      </Grid>
      <Loader isOpen={isFetching} />
    </Grid>
  );
};

const listProps = {
  isFetching: PropTypes.bool.description('toggle loading state'),
  hasMoreItems: PropTypes.bool
    .description('wheter or not you have more items that need to be loaded')
    .defaultValue(true),
  fetchData: PropTypes.func.description('callback to fetch new items'),
  children: PropTypes.element.description('row items for infinite loader'),
  height: PropTypes.number.description('infinite loader list height'),
} as WeakValidationMap<unknown>;

const InfiniteLoaderWithSchema = describe(InfiniteLoader);
InfiniteLoaderWithSchema.propTypes = listProps;
export { InfiniteLoader, InfiniteLoaderWithSchema };
